import React, {useEffect, useMemo, useState} from 'react';

import {GlobalCGEventTracker} from './core/analytics/tracker';
import type {Client} from './core/client/types';

export interface ClientState {
  client?: Client;
}

/**
 * The dependency-light context at the bottom of the app-wide compute graph
 * provider cascade. Keep this module free of panel implementations and remote
 * op loading so importing the provider does not pull either into the entry.
 */
export const ClientContext = React.createContext<ClientState>({
  client: undefined,
});
ClientContext.displayName = 'ClientContext';

let globalClient: Client | null = null;

export const getGlobalWeaveClient = () => {
  if (globalClient == null) {
    throw new Error('Global Weave Context not initialized');
  }
  return globalClient;
};

/**
 * Bridges a stable Client instance into ClientContext. Its loading subscription
 * re-renders this wrapper, but the memoized context value prevents consumer
 * notifications while the client reference is unchanged.
 */
export const ComputeGraphContextProviderFromClient: React.FC<{
  children?: React.ReactNode;
  client: Client;
}> = React.memo(({client, children}) => {
  const context = useMemo(() => ({client}), [client]);
  globalClient = client;

  const [isLoading, setIsLoading] = useState(false);
  // The observable is the client's imperative loading-state boundary.
  // oxlint-disable-next-line wandb/no-use-effect
  useEffect(() => {
    const subscription = context.client
      .loadingObservable()
      .subscribe(setIsLoading);
    return () => subscription.unsubscribe();
  }, [context]);

  return (
    <div
      data-test="compute-graph-provider"
      data-test-num-shadow-server-requests-counter={
        GlobalCGEventTracker.shadowServerRequests
      }
      className={isLoading ? 'loading cg-executing' : ''}
      style={{height: '100%'}}>
      <ClientContext.Provider value={context}>
        {children}
      </ClientContext.Provider>
    </div>
  );
});
ComputeGraphContextProviderFromClient.displayName =
  'ComputeGraphContextProviderFromClient';
